import { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import * as api from '../api/loginApi';
import { useLoginFlow } from '../contexts/LoginFlowContext';

import { usePolicyChecks } from './usePolicyChecks';

export const POLICY_CHANGED_MESSAGE = 'The documents were updated. Please review them again.';
export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

export const usePrivacyPolicy = () => {
    const { returnUrl } = useLoginFlow();
    const [error, setError] = useState<string | undefined>();

    const infoQuery = useQuery({
        queryKey: ['privacyPolicyInfo'],
        queryFn: api.getPrivacyPolicyInfo,
    });
    const info = infoQuery.data;
    const documents = (info?.required && info.documents) || [];
    const checks = usePolicyChecks(info?.email, documents);

    // Loop-breaker: if the SPA somehow lands on /privacy-policy while nothing is pending
    // (e.g. accepted via a race with another tab), go straight back rather than showing the page.
    useEffect(() => {
        if (info?.success && !info.required) {
            window.location.href = returnUrl;
        }
    }, [info, returnUrl]);

    const acceptMutation = useMutation({
        mutationFn: api.acceptPrivacyPolicy,
        onSuccess: (data) => {
            if (data.success) {
                checks.reset();
                window.location.href = returnUrl;
            } else if (data.error === 'policy.changed') {
                setError(POLICY_CHANGED_MESSAGE);
                infoQuery.refetch();
            } else {
                setError(GENERIC_ERROR_MESSAGE);
            }
        },
        onError: () => setError(GENERIC_ERROR_MESSAGE),
    });

    const declineMutation = useMutation({
        mutationFn: api.declinePrivacyPolicy,
        onSuccess: (data) => {
            checks.reset();
            window.location.href = data.redirectUrl ?? '/';
        },
        onError: () => {
            checks.reset();
            window.location.href = '/';
        },
    });

    return {
        isLoading: infoQuery.isLoading,
        companyName: info?.companyName,
        logoUrl: info?.logoUrl,
        alternateLogoUrl: info?.alternateLogoUrl,
        email: info?.email,
        title: info?.title,
        text: info?.text,
        documents,
        checkedTypes: checks.checkedTypes,
        isAccepting: acceptMutation.isPending,
        isDeclining: declineMutation.isPending,
        error,
        onCheckedChange: checks.onCheckedChange,
        onAccept: () => {
            setError(undefined);
            acceptMutation.mutate(documents.map(({ type, version }) => ({ type, version })));
        },
        onDecline: () => declineMutation.mutate(),
    };
};
